/**
 * @fileoverview Doctor Profile React Query Hook
 * @description Custom hook for doctor profile management with mutation support
 * @author MCHD Room Scheduler Team
 * @created 07-10-2025
 * @complies @ui-ux-excellence.mdc - React Query with mutations
 */

import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { api } from '../../api';
import type { DoctorProfile, DoctorProfileUpdateData, ApiResponse } from '../../types/doctor';

/**
 * React Query keys for doctor profile
 */
export const DOCTOR_PROFILE_KEYS = {
  all: ['doctor-profile'] as const,
  current: () => [...DOCTOR_PROFILE_KEYS.all, 'current'] as const,
} as const;

/**
 * Fetch current doctor's profile
 * @returns Promise with profile data
 */
async function getCurrentDoctorProfile(): Promise<ApiResponse<DoctorProfile>> {
  try {
    const response = await api.get('/dashboards/doctor/profile');
    
    // Convert date strings to Date objects
    if (response.success && response.data) {
      response.data = {
        ...response.data,
        lastLogin: response.data.lastLogin ? new Date(response.data.lastLogin) : null,
        createdAt: new Date(response.data.createdAt),
        updatedAt: new Date(response.data.updatedAt),
      };
    }
    
    return response;
  } catch (error) {
    console.error('[Doctor Profile] Error fetching profile:', error);
    throw error;
  }
}

/**
 * Update doctor profile
 * @param profileData - Updated profile data
 * @returns Promise with updated profile
 */
async function updateDoctorProfile(
  profileData: DoctorProfileUpdateData
): Promise<ApiResponse<DoctorProfile>> {
  try {
    const response = await api.put('/dashboards/doctor/profile', profileData);
    
    // Convert date strings to Date objects
    if (response.success && response.data) {
      response.data = {
        ...response.data,
        lastLogin: response.data.lastLogin ? new Date(response.data.lastLogin) : null,
        createdAt: new Date(response.data.createdAt),
        updatedAt: new Date(response.data.updatedAt),
      };
    }
    
    return response;
  } catch (error) {
    console.error('[Doctor Profile] Error updating profile:', error);
    throw error;
  }
}

/**
 * Hook for fetching current doctor's profile
 * @returns React Query result with profile data
 * 
 * @example
 * ```tsx
 * function DoctorProfilePage() {
 *   const { data, isLoading, error } = useDoctorProfile();
 * 
 *   if (isLoading) return <LoadingScreen />;
 *   if (error) return <ErrorDisplay />;
 * 
 *   const profile = data?.data;
 *   return <DoctorProfileForm profile={profile} />;
 * }
 * ```
 */
export function useDoctorProfile(): UseQueryResult<ApiResponse<DoctorProfile>, Error> {
  return useQuery({
    queryKey: DOCTOR_PROFILE_KEYS.current(),
    queryFn: getCurrentDoctorProfile,
    
    // Cache configuration
    staleTime: 10 * 60 * 1000, // 10 minutes - profile doesn't change often
    gcTime: 30 * 60 * 1000, // 30 minutes
    
    // Refetch configuration
    refetchOnWindowFocus: false, // Don't refetch on focus (profile is stable)
    refetchInterval: false, // No background refetch needed
    
    // Error handling
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    
    networkMode: 'online',
  });
}

/**
 * Hook for updating doctor profile
 * @returns Mutation result with update function
 * 
 * @example
 * ```tsx
 * function DoctorProfileForm() {
 *   const { mutate, isPending, error } = useUpdateDoctorProfile();
 *   
 *   const handleSubmit = (values) => {
 *     mutate(values, {
 *       onSuccess: () => {
 *         notifications.show({ message: 'Profile updated successfully' });
 *       },
 *       onError: (error) => {
 *         notifications.show({ message: error.message, color: 'red' });
 *       }
 *     });
 *   };
 * 
 *   return <Form onSubmit={handleSubmit} />;
 * }
 * ```
 */
export function useUpdateDoctorProfile(): UseMutationResult<
  ApiResponse<DoctorProfile>,
  Error,
  DoctorProfileUpdateData,
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateDoctorProfile,
    
    // Optimistic updates
    onMutate: async (newProfile) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: DOCTOR_PROFILE_KEYS.current() });

      // Snapshot the previous value
      const previousProfile = queryClient.getQueryData(DOCTOR_PROFILE_KEYS.current());

      // Optimistically update to the new value
      queryClient.setQueryData(
        DOCTOR_PROFILE_KEYS.current(),
        (old: ApiResponse<DoctorProfile> | undefined) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: {
              ...old.data,
              ...newProfile,
              updatedAt: new Date(),
            },
          };
        }
      );

      // Return context with the snapshot
      return { previousProfile };
    },

    // On error, roll back to the previous value
    onError: (_err, _newProfile, context: any) => {
      if (context?.previousProfile) {
        queryClient.setQueryData(DOCTOR_PROFILE_KEYS.current(), context.previousProfile);
      }
    },

    // Always refetch after error or success to ensure sync with server
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: DOCTOR_PROFILE_KEYS.current() });
    },
  });
}

/**
 * Extract profile data safely from hook result
 * @param result - React Query result from useDoctorProfile
 * @returns Profile data or null if not available
 */
export function extractProfile(
  result: UseQueryResult<ApiResponse<DoctorProfile>, Error>
): DoctorProfile | null {
  if (result.data?.success && result.data.data) {
    return result.data.data;
  }
  return null;
}

/**
 * Get error message from profile query result
 * @param result - React Query result
 * @returns User-friendly error message or null
 */
export function getProfileError(
  result: UseQueryResult<ApiResponse<DoctorProfile>, Error> | UseMutationResult<ApiResponse<DoctorProfile>, Error, DoctorProfileUpdateData, unknown>
): string | null {
  if (result.error) {
    return result.error.message || 'Failed to load profile data';
  }
  
  if ('data' in result && result.data && !result.data.success && result.data.error) {
    return result.data.error.message || 'Failed to load profile data';
  }
  
  return null;
}

/**
 * Check if profile has complete data
 * @param profile - Profile data
 * @returns True if profile has specialty and department
 */
export function hasCompleteProfile(profile: DoctorProfile | null): boolean {
  if (!profile) return false;
  return !!(profile.specialty && profile.department);
}

